import { Injectable } from '@angular/core';
import {
  Firestore,
  collection,
  CollectionReference,
  DocumentData,
  addDoc,
  getDocs,
  query,
  orderBy,
  collectionData,collectionGroup, doc, getDoc, limit, QueryDocumentSnapshot, startAfter, Timestamp, where
} from '@angular/fire/firestore';
import { AuthService } from '../services/auth.service';
import { Payment } from '../models/payment.model';
import { combineLatest, from, map, Observable, of, switchMap, tap  } from 'rxjs';
import { Functions, httpsCallable } from '@angular/fire/functions';
import { endOfMonth, startOfMonth } from 'date-fns';
import { RentalService } from './rental.service';
import { UserService } from './user.service';

@Injectable({
  providedIn: 'root'
})
export class PaymentService {

  constructor(private firestore: Firestore,private auth:AuthService,private functions: Functions, private rentalService: RentalService,private userService: UserService) {}

  /** Helper: get the payments subcollection for a user */
  private getUserPaymentsRef(uid: string): CollectionReference<DocumentData> {
    return collection(this.firestore, `users/${uid}/payments`);
  }
  private normalizeDate(input: any): Date | null {
    if (!input) return null;

    // Firestore Timestamp
    if (input instanceof Timestamp) return input.toDate();

    // Number timestamp
    if (typeof input === 'number') return new Date(input);

    // String timestamp (e.g. "November 18, 2025 at 1:30:06 PM UTC")
    if (typeof input === 'string') return new Date(input);

    // Fallback
    return null;
  }
  getPaymentsWithDetails(
  landlordId: string
): Observable<any[]> {

  return combineLatest([
    this.getPaymentsByLandlordIdThisMonth(landlordId),
    this.userService.getUsers(),
    this.rentalService.getRentals()
  ]).pipe(
    map(([payments, users, rentals]) =>
      payments.map(payment => {
        const user = users.find(u => u.uid === payment.uid);
        const rental = rentals.find(r => r.id === user?.rentalId);

        return {
          id: payment.id,
          amount: payment.amount,
          status: payment.status,
          email: user?.email ?? '—',
          userName: user
            ? `${user.firstName} ${user.lastName}`
            : 'Unknown',
          rentalAddress: rental?.address ?? '—',
          createdAt: this.normalizeDate(payment.createdAt)
        };
      })
    )
  );
}
getPaymentsByLandlordIdThisMonth(
  landlordId: string
): Observable<any[]> {

  const { start, end } = this.getMonthRange();

  const q = query(
    collectionGroup(this.firestore, 'payments'),
    where('landlordId', '==', landlordId),
    where('createdAt', '>=', start),
    where('createdAt', '<=', end)
  );

  return collectionData(q, { idField: 'id' }).pipe(
    map((payments: any[]) =>
      payments.map(p => ({
        ...p,
        uid: p.uid ?? p.tenantId // keep compatibility
      }))
    )
  );
}
 getPaymentsByLandlordId(landlordId: string): Observable<any[]> {
    console.log('[PaymentService] Querying payments for landlord:', landlordId);

    const paymentsQuery = query(
      collectionGroup(this.firestore, 'payments'),
      where('landlordId', '==', landlordId)
    );

    return collectionData(paymentsQuery, { idField: 'id' }).pipe(
      tap(payments => {
        console.log(
          `[PaymentService] Payments returned (${payments.length})`,
          payments
        );
      })
    );
  }
  /** Format Firestore timestamp to readable date string */
  private formatDate(timestamp: any): string {
    if (!timestamp) return '';
    const date = timestamp instanceof Timestamp ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  }
// payment.service.ts
getRecentPaymentsByTenant(uid: string): Observable<any[]> {
  const ref = collection(this.firestore, `users/${uid}/payments`);
  const q = query(
    ref,
    orderBy('createdAt', 'desc'),
    limit(6)
  );

  return collectionData(q, { idField: 'id' }) as Observable<any[]>;
}
  getPaymentsByTenant(uid: string): Observable<any[]> {
    const ref = collection(this.firestore, `users/${uid}/payments`);
    const q = query(ref, orderBy('createdAt', 'desc'));  // <-- SORTED DESC

    return collectionData(q, { idField: 'id' }) as Observable<any[]>;
  }

  /* --------------------------------------------
   * Payments THIS MONTH (admin)
   * -------------------------------------------- */
  getPaymentsThisMonth(
    landlordId?: string
  ): Observable<Payment[]> {
    const now = new Date();
    const start = new Date(now.getFullYear(), now.getMonth(), 1);
    const end = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59);

    const paymentsQuery = query(
      collectionGroup(this.firestore, 'payments'),
      where('createdAt', '>=', start),
      where('createdAt', '<=', end),
      where('status', '==', 'paid')
    );

    return from(getDocs(paymentsQuery)).pipe(
      map(snapshot =>
        snapshot.docs.map(d => ({
          id: d.id,
          ...(d.data() as Payment)
        }))
      ),
      switchMap(payments => {
        if (!landlordId) return [payments];

        return this.filterPaymentsByLandlord(payments, landlordId);
      })
    );
  }
  getPaymentsChartByLandlordIds(landlordIds: string[]) {

  if (!landlordIds.length) {
    return of({ labels: [], totals: [] });
  }

  return collectionData(
    query(
      collectionGroup(this.firestore, 'payments'),
      where('landlordId', 'in', landlordIds)
    )
  ).pipe(
    map((payments: any[]) => {
      const totalsByDay: Record<string, number> = {};

      payments.forEach(p => {
        if (!p.createdAt?.toDate) return;

        const day = p.createdAt.toDate().toISOString().slice(0, 10);
        totalsByDay[day] = (totalsByDay[day] || 0) + (p.amount ?? 0) / 100;
      });

      return {
        labels: Object.keys(totalsByDay),
        totals: Object.values(totalsByDay)
      };
    }),
  );
}
private getMonthRange(): { start: Timestamp; end: Timestamp } {
  const now = new Date();

  const startOfMonth = new Date(
    now.getFullYear(),
    now.getMonth(),
    1,
    0, 0, 0, 0
  );

  return {
    start: Timestamp.fromDate(startOfMonth),
    end: Timestamp.fromDate(now)
  };
}
getPaymentStatusBreakdownByLandlordIds(
  landlordIds: string[]
): Observable<{ label: string; total: number }[]> {

  if (!landlordIds.length) return of([]);

  const { start, end } = this.getMonthRange();

  const q = query(
    collectionGroup(this.firestore, 'payments'),
    where('landlordId', 'in', landlordIds),
    where('createdAt', '>=', start),
    where('createdAt', '<=', end)
  );

  return collectionData(q).pipe(
    map((payments: any[]) => {
      const totals = {
        paid: 0,
        pending: 0,
        failed: 0
      };

      payments.forEach(p => {
        const amount = (p.amount ?? 0) / 100;

        if (p.status === 'paid' || p.status === 'succeeded') {
          totals.paid += amount;
        } else if (p.status === 'pending') {
          totals.pending += amount;
        } else {
          totals.failed += amount;
        }
      });

      return [
        { label: 'Paid', total: totals.paid },
        { label: 'Pending', total: totals.pending },
        { label: 'Failed', total: totals.failed }
      ];
    })
  );
}


  private aggregatePaymentsByMonth(
    payments: Payment[],
    monthsBack: number
  ) {
    const buckets: Record<string, number> = {};

    // Initialize months
    for (let i = monthsBack - 1; i >= 0; i--) {
      const d = new Date();
      d.setMonth(d.getMonth() - i);
      const key = `${d.getFullYear()}-${d.getMonth()}`;
      buckets[key] = 0;
    }

    // Sum payments
    for (const payment of payments) {
      const date = new Date(payment.createdAt);
      const key = `${date.getFullYear()}-${date.getMonth()}`;

      if (key in buckets && payment.status === 'paid') {
        buckets[key] += payment.amount / 100; // cents → dollars
      }
    }

    const labels: string[] = [];
    const totals: number[] = [];

    Object.keys(buckets).forEach(key => {
      const [year, month] = key.split('-').map(Number);
      labels.push(
        new Date(year, month).toLocaleString('default', { month: 'short' })
      );
      totals.push(buckets[key]);
    });

    return { labels, totals };
  }
  getPaymentsThisMonthForLandlords(
  landlordIds: string[]
): Observable<number> {

  if (!landlordIds.length) return of(0);

  const now = new Date();
  const startOfMonth = new Date(
    now.getFullYear(),
    now.getMonth(),
    1
  );

  return collectionData(
    collectionGroup(this.firestore, 'payments'),
    { idField: 'id' }
  ).pipe(
    map((payments: any[]) =>
      payments
        .filter(p =>
          landlordIds.includes(p.landlordId) &&
          p.createdAt?.toDate &&
          p.createdAt.toDate() >= startOfMonth &&
          p.status === 'succeeded'
        )
        .reduce((sum, p) => sum + (p.amount ?? 0), 0) / 100
    )
  );
}



getAllPaymentsRaw(): Observable<any[]> {
    const paymentsRef = collectionGroup(this.firestore, 'payments');

    return collectionData(paymentsRef, {
      idField: 'id'
    });
  }

  /* --------------------------------------------
   * Chart data (daily totals)
   * -------------------------------------------- */
  getPaymentsChartDataForLandlords(
  landlordIds: string[]
): Observable<{ date: string; total: number }[]> {

  if (!landlordIds.length) return of([]);

  const now = new Date();
  const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);

  const paymentsQuery = query(
    collectionGroup(this.firestore, 'payments'),
    where('createdAt', '>=', startOfMonth)
  );

  return from(getDocs(paymentsQuery)).pipe(
    switchMap(async snapshot => {
      const grouped = new Map<string, number>();

      for (const docSnap of snapshot.docs) {
        const payment = docSnap.data() as any;
        const userId = docSnap.ref.parent.parent?.id;
        if (!userId) continue;

        const userSnap = await getDoc(
          doc(this.firestore, 'users', userId)
        );
        if (!userSnap.exists()) continue;

        const user = userSnap.data() as any;
        if (!user.rentalId) continue;

        const rentalSnap = await getDoc(
          doc(this.firestore, 'rentals', user.rentalId)
        );
        if (!rentalSnap.exists()) continue;

        const rental = rentalSnap.data() as any;

        if (!landlordIds.includes(rental.landlordId)) continue;

        const dateKey =
          payment.createdAt.toDate().toISOString().split('T')[0];

        grouped.set(
          dateKey,
          (grouped.get(dateKey) || 0) + payment.amount
        );
      }

      return Array.from(grouped.entries())
        .map(([date, total]) => ({
          date,
          total: total / 100
        }))
        .sort((a, b) => a.date.localeCompare(b.date));
    })
  );
}


  /* --------------------------------------------
   * Internal helper
   * -------------------------------------------- */
  private filterPaymentsByLandlord(
    payments: Payment[],
    landlordId: string
  ): Observable<Payment[]> {
    const rentalIds = [...new Set(payments.map(p => p.rentalId))];

    return this.rentalService.getRentalsByIds(rentalIds).pipe(
      map(rentals => {
        const allowed = new Set(
          rentals
            .filter(r => r.landlordId === landlordId)
            .map(r => r.id)
        );

        return payments.filter(p => allowed.has(p.rentalId));
      })
    );
  }
  getMonthlyPaymentsByDay(
  landlordId?: string,
  monthOffset = 0
): Observable<{ date: string; total: number }[]> {

  const baseDate = new Date();
  baseDate.setMonth(baseDate.getMonth() + monthOffset);

  const start = Timestamp.fromDate(
    new Date(baseDate.getFullYear(), baseDate.getMonth(), 1)
  );

  const end = Timestamp.fromDate(
    new Date(baseDate.getFullYear(), baseDate.getMonth() + 1, 0, 23, 59, 59)
  );

  let q = query(
    collectionGroup(this.firestore, 'payments'),
    where('createdAt', '>=', start),
    where('createdAt', '<=', end)
  );

  if (landlordId) {
    q = query(q, where('landlordId', '==', landlordId));
  }

  return from(getDocs(q)).pipe(
    map(snapshot => {
      const daily: Record<string, number> = {};

      snapshot.forEach(doc => {
        const d = doc.data();
        const day = d.createdAt
          .toDate()
          .toISOString()
          .slice(0, 10);

        daily[day] = (daily[day] || 0) + d.amount;
      });

      return Object.entries(daily).map(([date, total]) => ({
        date,
        total: total / 100
      }));
    })
  );
}

  /** Add a new payment for a tenant */
  async addPayment(uid: string, payment: Omit<Payment, 'id'>): Promise<void> {
    const paymentsRef = this.getUserPaymentsRef(uid);
    await addDoc(paymentsRef, {
      ...payment,
      createdAt: payment.createdAt || Date.now()
    });
  }
  async downloadReceipt(payment: any, p0: string) {
    if (payment.brandedReceiptUrl) {
      window.open(payment.brandedReceiptUrl, '_blank');
      return;
    }

    const fn = httpsCallable(this.functions, 'generateReceiptPdf');

    const res: any = await fn({
      paymentId: payment.id,
      uid: this.auth.firebaseUid
    });

    window.open(res.data.url, '_blank');
  }

  getPaymentsPage(
  uid: string,
  pageSize = 10,
  startAfterDoc?: QueryDocumentSnapshot
): Observable<{ payments: any[]; lastDoc: QueryDocumentSnapshot | null }> {

  const ref = collection(this.firestore, `users/${uid}/payments`);

  const q = startAfterDoc
    ? query(ref, orderBy('createdAt', 'desc'), startAfter(startAfterDoc), limit(pageSize))
    : query(ref, orderBy('createdAt', 'desc'), limit(pageSize));

  return new Observable(sub => {
    getDocs(q).then(snapshot => {
      sub.next({
        payments: snapshot.docs.map(d => ({ id: d.id, ...d.data() })),
        lastDoc: snapshot.docs[snapshot.docs.length - 1] || null
      });
      sub.complete();
    });
  });
}

}
